// src/components/blog/BlogPostComments.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  fetchBlogComments,
  updateCommentStatus,
  deleteComment,
  fetchBlogPost,
  clearBlogPostError,
} from '../../store/actions/blogPostActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import Pagination from '../common/Pagination';
import SearchBar from '../common/SearchBar';

const BlogPostComments = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  
  const { 
    selectedBlogPost,
    comments,
    commentsMeta,
    loading,
    error 
  } = useSelector((state) => state.blogPosts);

  const [selectedComment, setSelectedComment] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [commentStatus, setCommentStatus] = useState('');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [limit] = useState(10);

  const commentStatusOptions = [
    { value: 'PENDING', label: 'En attente', color: '#f59e0b', bg: '#fef3c7' },
    { value: 'APPROVED', label: 'Approuvé', color: '#10b981', bg: '#d1fae5' },
    { value: 'REJECTED', label: 'Rejeté', color: '#dc2626', bg: '#fee2e2' },
    { value: 'SPAM', label: 'Spam', color: '#6b7280', bg: '#f3f4f6' },
  ];

  // Styles object
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      marginBottom: '2rem',
    },
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    backButton: {
      padding: '0.5rem',
      backgroundColor: '#f3f4f6',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
    },
    backButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    postInfo: {
      marginTop: '1rem',
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    postTitle: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
    },
    postMeta: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginTop: '0.25rem',
    },
    viewPostButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      textDecoration: 'none',
      transition: 'all 0.2s',
    },
    controls: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    filters: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap',
    },
    filterButton: {
      padding: '0.5rem 1rem',
      background: 'none',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      cursor: 'pointer',
      color: '#374151',
      transition: 'all 0.2s',
    },
    filterButtonActive: {
      backgroundColor: '#2563eb',
      color: '#ffffff',
      borderColor: '#2563eb',
    },
    statsBar: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '1rem',
      marginBottom: '2rem',
      padding: '1.5rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
    },
    statCard: {
      textAlign: 'center',
    },
    statValue: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '0.25rem',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    commentsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      marginBottom: '2rem',
    },
    commentCard: {
      padding: '1.5rem',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '0.5rem',
      transition: 'all 0.2s',
    },
    commentHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '1rem',
    },
    commentAuthor: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    commentAvatar: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      backgroundColor: '#e5e7eb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.25rem',
      fontWeight: 500,
      color: '#6b7280',
    },
    commentAuthorInfo: {
      display: 'flex',
      flexDirection: 'column',
    },
    commentAuthorName: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#111827',
    },
    commentAuthorEmail: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    commentDate: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    commentContent: {
      fontSize: '0.875rem',
      color: '#374151',
      lineHeight: '1.6',
      marginBottom: '1rem',
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.375rem',
    },
    commentFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    commentStatus: {
      display: 'inline-flex',
      padding: '0.25rem 0.75rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      borderRadius: '9999px',
    },
    commentActions: {
      display: 'flex',
      gap: '0.5rem',
    },
    actionButton: {
      padding: '0.375rem 0.75rem',
      background: 'none',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      fontSize: '0.75rem',
      cursor: 'pointer',
      color: '#374151',
      transition: 'all 0.2s',
    },
    actionButtonHover: {
      backgroundColor: '#f3f4f6',
    },
    approveButton: {
      color: '#10b981',
      borderColor: '#10b981',
    },
    rejectButton: {
      color: '#dc2626',
      borderColor: '#dc2626',
    },
    spamButton: {
      color: '#6b7280',
      borderColor: '#6b7280',
    },
    deleteButton: {
      color: '#dc2626',
      borderColor: '#dc2626',
    },
    replyInfo: {
      marginTop: '1rem',
      padding: '1rem',
      backgroundColor: '#f3f4f6',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      borderLeft: '3px solid #2563eb',
    },
    replyLabel: {
      fontWeight: 600,
      color: '#2563eb',
      marginBottom: '0.25rem',
    },
    replyContent: {
      color: '#374151',
    },
    emptyState: {
      textAlign: 'center',
      padding: '4rem 2rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      border: '2px dashed #e5e7eb',
    },
    emptyIcon: {
      width: '4rem',
      height: '4rem',
      marginBottom: '1rem',
      color: '#9ca3af',
    },
    emptyTitle: {
      fontSize: '1.125rem',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '0.5rem',
    },
    emptyText: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    statusModal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    statusModalContent: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      padding: '2rem',
      maxWidth: '400px',
      width: '90%',
    },
    statusModalTitle: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1.5rem',
    },
    statusOptions: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
      marginBottom: '2rem',
    },
    statusOption: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.75rem',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    statusOptionSelected: {
      borderColor: '#2563eb',
      backgroundColor: '#eff6ff',
    },
    statusOptionRadio: {
      width: '1rem',
      height: '1rem',
      cursor: 'pointer',
    },
    modalButtons: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
    },
    saveButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
  };

  const getCommentStatusStyle = (status) => {
    const option = commentStatusOptions.find(s => s.value === status);
    return {
      backgroundColor: option?.bg || '#f3f4f6',
      color: option?.color || '#374151',
    };
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchBlogPost(id));
      fetchComments();
    }
  }, [dispatch, id]);

  useEffect(() => {
    fetchComments();
  }, [page, statusFilter, searchTerm]);

  const fetchComments = () => {
    const params = {
      page,
      limit,
      status: statusFilter || undefined,
      search: searchTerm || undefined,
    };
    dispatch(fetchBlogComments(id, params));
  };

  const handleStatusUpdate = useCallback(async () => {
    try {
      await dispatch(updateCommentStatus(id, selectedComment.id, commentStatus));
      setShowStatusModal(false);
      setSelectedComment(null);
      setCommentStatus('');
      fetchComments();
    } catch (error) {
      console.error('Status update failed:', error);
    }
  }, [dispatch, id, selectedComment, commentStatus]);

  const handleDelete = useCallback(async () => {
    try {
      await dispatch(deleteComment(id, selectedComment.id));
      setShowDeleteConfirm(false);
      setSelectedComment(null);
      fetchComments();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, id, selectedComment]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setPage(1);
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCommentStats = () => {
    if (!comments) return { total: 0, pending: 0, approved: 0, rejected: 0, spam: 0 };
    
    return comments.reduce((acc, comment) => {
      acc.total++;
      if (comment.status === 'PENDING') acc.pending++;
      else if (comment.status === 'APPROVED') acc.approved++;
      else if (comment.status === 'REJECTED') acc.rejected++;
      else if (comment.status === 'SPAM') acc.spam++;
      return acc;
    }, { total: 0, pending: 0, approved: 0, rejected: 0, spam: 0 });
  };

  const stats = getCommentStats();

  if (loading && !comments) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div style={styles.headerLeft}>
            <button
              onClick={() => navigate(`/admin/blog-posts/${id}/details`)}
              style={styles.backButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = styles.backButton.backgroundColor;
              }}
            >
              <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h1 style={styles.title}>Gestion des commentaires</h1>
          </div>
        </div>

        {selectedBlogPost && (
          <div style={styles.postInfo}>
            <div>
              <div style={styles.postTitle}>{selectedBlogPost.title}</div>
              <div style={styles.postMeta}>
                Publié le {formatDate(selectedBlogPost.publishedAt || selectedBlogPost.createdAt)} • 
                {selectedBlogPost._count?.comments || 0} commentaire(s)
              </div>
            </div>
            <Link
              to={`/admin/blog/${id}/details`}
              style={styles.viewPostButton}
            >
              Voir l'article
            </Link>
          </div>
        )}
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearBlogPostError())}
        />
      )}

      {/* Stats Cards */}
      <div style={styles.statsBar}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.total}</div>
          <div style={styles.statLabel}>Total</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.pending}</div>
          <div style={styles.statLabel}>En attente</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.approved}</div>
          <div style={styles.statLabel}>Approuvés</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.rejected}</div>
          <div style={styles.statLabel}>Rejetés</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.spam}</div>
          <div style={styles.statLabel}>Spam</div>
        </div>
      </div>

      {/* Controls */}
      <div style={styles.controls}>
        <div style={styles.filters}>
          <button
            style={{
              ...styles.filterButton,
              ...(!statusFilter ? styles.filterButtonActive : {}),
            }}
            onClick={() => {
              setStatusFilter('');
              setPage(1);
            }}
          >
            Tous
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(statusFilter === 'PENDING' ? styles.filterButtonActive : {}),
            }}
            onClick={() => {
              setStatusFilter('PENDING');
              setPage(1);
            }}
          >
            En attente
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(statusFilter === 'APPROVED' ? styles.filterButtonActive : {}),
            }}
            onClick={() => {
              setStatusFilter('APPROVED');
              setPage(1);
            }}
          >
            Approuvés
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(statusFilter === 'REJECTED' ? styles.filterButtonActive : {}),
            }}
            onClick={() => {
              setStatusFilter('REJECTED');
              setPage(1);
            }}
          >
            Rejetés
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(statusFilter === 'SPAM' ? styles.filterButtonActive : {}),
            }}
            onClick={() => {
              setStatusFilter('SPAM');
              setPage(1);
            }}
          >
            Spam
          </button>
        </div>
        <div style={{ width: '300px' }}>
          <SearchBar
            onSearch={handleSearch}
            placeholder="Rechercher un commentaire..."
          />
        </div>
      </div>

      {/* Comments List */}
      {comments && comments.length > 0 ? (
        <div style={styles.commentsList}>
          {comments.map((comment) => (
            <div key={comment.id} style={styles.commentCard}>
              <div style={styles.commentHeader}>
                <div style={styles.commentAuthor}>
                  <div style={styles.commentAvatar}>
                    {comment.authorName?.charAt(0).toUpperCase()}
                  </div>
                  <div style={styles.commentAuthorInfo}>
                    <span style={styles.commentAuthorName}>
                      {comment.authorName}
                      {comment.user && (
                        <span style={{ fontSize: '0.75rem', color: '#2563eb', marginLeft: '0.5rem' }}>
                          (Utilisateur inscrit)
                        </span>
                      )}
                    </span>
                    <span style={styles.commentAuthorEmail}>
                      {comment.authorEmail}
                      {comment.authorWebsite && (
                        <span> • <a href={comment.authorWebsite} target="_blank" rel="noopener noreferrer">Site web</a></span>
                      )}
                    </span>
                  </div>
                </div>
                <span style={styles.commentDate}>
                  {formatDate(comment.createdAt)}
                </span>
              </div>

              <div style={styles.commentContent}>
                {comment.content}
              </div>

              {comment.parent && (
                <div style={styles.replyInfo}>
                  <div style={styles.replyLabel}>En réponse à :</div>
                  <div style={styles.replyContent}>
                    <strong>{comment.parent.authorName}</strong> • {comment.parent.content.substring(0, 100)}...
                  </div>
                </div>
              )}

              <div style={styles.commentFooter}>
                <span
                  style={{
                    ...styles.commentStatus,
                    ...getCommentStatusStyle(comment.status),
                  }}
                >
                  {commentStatusOptions.find(s => s.value === comment.status)?.label || comment.status}
                </span>

                <div style={styles.commentActions}>
                  {comment.status !== 'APPROVED' && (
                    <button
                      style={{
                        ...styles.actionButton,
                        ...styles.approveButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#d1fae5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      onClick={() => {
                        setSelectedComment(comment);
                        setCommentStatus('APPROVED');
                        setShowStatusModal(true);
                      }}
                    >
                      ✓ Approuver
                    </button>
                  )}
                  {comment.status !== 'REJECTED' && (
                    <button
                      style={{
                        ...styles.actionButton,
                        ...styles.rejectButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#fee2e2';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      onClick={() => {
                        setSelectedComment(comment);
                        setCommentStatus('REJECTED');
                        setShowStatusModal(true);
                      }}
                    >
                      ✗ Rejeter
                    </button>
                  )}
                  {comment.status !== 'SPAM' && (
                    <button
                      style={{
                        ...styles.actionButton,
                        ...styles.spamButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      onClick={() => {
                        setSelectedComment(comment);
                        setCommentStatus('SPAM');
                        setShowStatusModal(true);
                      }}
                    >
                      🚫 Spam
                    </button>
                  )}
                  <button
                    style={{
                      ...styles.actionButton,
                      ...styles.deleteButton,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#fee2e2';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    onClick={() => {
                      setSelectedComment(comment);
                      setShowDeleteConfirm(true);
                    }}
                  >
                    🗑️ Supprimer
                  </button>
                </div>
              </div>

              {comment.userAgent && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: '#9ca3af' }}>
                  User Agent: {comment.userAgent} • IP: {comment.userIp}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.emptyState}>
          <svg style={styles.emptyIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <div style={styles.emptyTitle}>
            {searchTerm || statusFilter 
              ? 'Aucun commentaire trouvé'
              : 'Aucun commentaire pour cet article'}
          </div>
          <div style={styles.emptyText}>
            {searchTerm || statusFilter 
              ? 'Essayez de modifier vos filtres de recherche'
              : 'Les commentaires apparaîtront ici lorsque des visiteurs en laisseront'}
          </div>
        </div>
      )}

      {/* Pagination */}
      {commentsMeta && commentsMeta.totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={commentsMeta.totalPages}
          onPageChange={setPage}
        />
      )}

      {/* Status Update Modal */}
      {showStatusModal && (
        <div style={styles.statusModal} onClick={() => setShowStatusModal(false)}>
          <div style={styles.statusModalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.statusModalTitle}>
              Changer le statut du commentaire
            </h3>
            <div style={styles.statusOptions}>
              {commentStatusOptions.map((option) => (
                <div
                  key={option.value}
                  style={{
                    ...styles.statusOption,
                    ...(commentStatus === option.value ? styles.statusOptionSelected : {}),
                  }}
                  onClick={() => setCommentStatus(option.value)}
                >
                  <input
                    type="radio"
                    name="commentStatus"
                    value={option.value}
                    checked={commentStatus === option.value}
                    onChange={() => setCommentStatus(option.value)}
                    style={styles.statusOptionRadio}
                  />
                  <span style={{ flex: 1 }}>{option.label}</span>
                  <span
                    style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      backgroundColor: option.bg,
                      color: option.color,
                      fontSize: '0.75rem',
                    }}
                  >
                    {option.label}
                  </span>
                </div>
              ))}
            </div>
            <div style={styles.modalButtons}>
              <button
                onClick={() => setShowStatusModal(false)}
                style={styles.cancelButton}
              >
                Annuler
              </button>
              <button
                onClick={handleStatusUpdate}
                disabled={commentStatus === selectedComment?.status}
                style={{
                  ...styles.saveButton,
                  opacity: commentStatus === selectedComment?.status ? 0.5 : 1,
                  cursor: commentStatus === selectedComment?.status ? 'not-allowed' : 'pointer',
                }}
              >
                Mettre à jour
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setSelectedComment(null);
        }}
        onConfirm={handleDelete}
        title="Supprimer le commentaire"
        message="Êtes-vous sûr de vouloir supprimer ce commentaire ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default BlogPostComments;